import type { ReactNode } from 'react';

/**
 * La superficie de marca: navy con degradado y la textura de las dos cápsulas.
 *
 * Es donde la marca aparece sin gritar. Las dos cápsulas del isotipo —una en
 * contorno, una llena— se dibujan con los pseudo-elementos y los velos blancos
 * de `tokens.css` (`veil-*`), **nunca con una imagen**: así escalan, no pesan y
 * no hay un PNG que se vea borroso en una pantalla de alta densidad.
 *
 * Variantes, con el nombre de clase de la referencia entre paréntesis:
 *
 * - `inicio` (hero): el encabezado del inicio, pasos y perfil del paciente, con
 *   los bordes de abajo redondeados para que las tarjetas se monten encima.
 * - `compacto` (hero compact): lo mismo con menos aire abajo.
 * - `portada` (splash): la pantalla de acceso, a pantalla completa.
 * - `caso` (case-head): el encabezado de la ficha del caso, en el workspace.
 * - `caso-fino` (case-head slim): el mismo, fino y sin textura, en las pestañas.
 * - `lateral` (side): el sidebar del equipo.
 * - `barra`: la barra superior del equipo en el teléfono, donde el sidebar no
 *   entra (E3.17). Sin textura: es una franja de 56 px y las cápsulas no se
 *   leerían.
 *
 * Todo texto sobre esta superficie va en blanco o navy-100: los dos pasan AA
 * sobre cualquier punto del degradado (el peor caso, navy-100 sobre navy, da
 * 7,4:1). El anillo de foco adentro es teal (`foco-sobre-oscuro`): el navy del
 * sistema no se vería sobre navy.
 */

type Variante = 'inicio' | 'compacto' | 'portada' | 'caso' | 'caso-fino' | 'lateral' | 'barra';

const CAPSULAS =
  'before:pointer-events-none before:absolute before:rounded-full after:pointer-events-none after:absolute after:rounded-full';

const VARIANTE: Readonly<Record<Variante, string>> = Object.freeze({
  inicio: `rounded-b-4xl bg-linear-155 from-brand to-brand-strong to-78% px-5 pt-6 pb-12 ${CAPSULAS} before:-top-11.5 before:right-23 before:h-37.5 before:w-21 before:border-3 before:border-veil-2 after:-top-4.5 after:-right-5.5 after:h-51 after:w-26 after:bg-veil-2`,
  compacto: `rounded-b-4xl bg-linear-155 from-brand to-brand-strong to-78% px-5 pt-6 pb-8 ${CAPSULAS} before:-top-11.5 before:right-23 before:h-37.5 before:w-21 before:border-3 before:border-veil-2 after:-top-4.5 after:-right-5.5 after:h-51 after:w-26 after:bg-veil-2`,
  portada: `flex flex-col items-center justify-center bg-linear-165 from-brand to-brand-strong to-70% p-8 text-center ${CAPSULAS} before:-bottom-17.5 before:-left-12.5 before:h-90 before:w-42.5 before:border-4 before:border-veil-2 after:-top-20 after:-right-15 after:h-97.5 after:w-47.5 after:bg-veil-1`,
  caso: `rounded-xl bg-linear-120 from-brand to-brand-strong to-85% px-8 py-6 shadow-glow-navy-dark ${CAPSULAS} before:-top-17.5 before:right-152.5 before:h-37.5 before:w-17.5 before:border-3 before:border-veil-2 after:top-10 after:right-130 after:h-42.5 after:w-21 after:bg-veil-1`,
  'caso-fino':
    'rounded-xl bg-linear-120 from-brand to-brand-strong to-85% px-8 py-4 shadow-glow-navy-dark',
  barra: 'bg-linear-180 from-brand-strong to-ink px-4 py-3',
  lateral: `bg-linear-180 from-brand-strong to-ink px-4 py-6 ${CAPSULAS} before:bottom-24 before:left-8.5 before:h-52.5 before:w-24 before:border-3 before:border-veil-1 after:bottom-42.5 after:left-34.5 after:h-57.5 after:w-27.5 after:bg-veil-1`,
});

export function Hero({
  variante = 'inicio',
  elemento: Elemento = 'div',
  className = '',
  children,
}: {
  variante?: Variante;
  elemento?: 'div' | 'header' | 'section' | 'aside';
  className?: string;
  children: ReactNode;
}) {
  return (
    <Elemento
      className={`foco-sobre-oscuro relative shrink-0 overflow-hidden text-primary-foreground *:relative *:z-1 ${VARIANTE[variante]} ${className}`}
    >
      {children}
    </Elemento>
  );
}
